import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ReportsGate from '../components/ReportsGate';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

function formatKg(value) {
  const n = Number(value) || 0;
  if (n >= 1000) return `${(n / 1000).toLocaleString('en-AU', { maximumFractionDigits: 2 })} t CO₂e`;
  if (n >= 10) return `${n.toLocaleString('en-AU', { maximumFractionDigits: 1 })} kg CO₂e`;
  return `${n.toLocaleString('en-AU', { maximumFractionDigits: 2 })} kg CO₂e`;
}

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch (_) {
    return '—';
  }
}

function CustomerFootprintReportDetailInner() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendSuccess, setResendSuccess] = useState('');

  const handleResend = async () => {
    setResending(true);
    setResendError('');
    setResendSuccess('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/reports/customer-footprint/${id}/resend`);
      const sentAt = data?.sent_at || new Date().toISOString();
      setReport((prev) => (prev ? { ...prev, sent_at: sentAt } : prev));
      setResendSuccess(`Email sent to ${report?.customer_email_snapshot || 'customer'}.`);
    } catch (err) {
      setResendError(err?.response?.data?.error || 'Failed to resend report');
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const { data } = await axios.get(`${API_BASE_URL}/reports/customer-footprint/${id}`);
        if (cancelled) return;
        const row = data?.data || null;
        setReport(row);
        if (row?.line_items?.length) {
          const previewBody = {
            customerId: row.customer_id,
            lineItems: row.line_items.map((it) => ({
              productId: it.product_id,
              quantity: it.quantity,
            })),
            introMessage: row.intro_message || '',
          };
          try {
            const { data: preview } = await axios.post(
              `${API_BASE_URL}/reports/customer-footprint/preview`,
              previewBody
            );
            if (!cancelled) setPreviewHtml(preview?.data?.html || '');
          } catch (_) {
            // Preview is best-effort — if the customer was deleted or a product
            // is no longer available, fall back to the line item summary below.
          }
        }
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Failed to load report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="p-3 d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3" style={{ maxWidth: 800, margin: '0 auto' }}>
        <div className="alert alert-danger" role="alert">{error}</div>
        <Link to="/reports/customer-footprint" className="btn btn-outline-secondary">
          &larr; Back to reports
        </Link>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="p-3" style={{ maxWidth: 900, margin: '0 auto' }}>
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <Link to="/reports/customer-footprint" className="text-muted text-decoration-none" style={{ fontSize: 13 }}>
            &larr; Customer Footprint Reports
          </Link>
          <h1 className="fw-bold mb-1 mt-1">Report to {report.customer_name_snapshot}</h1>
          <p className="text-muted mb-0">
            {report.sent_at ? 'Sent ' : 'Created '}
            {formatDate(report.sent_at || report.created_at)} to {report.customer_email_snapshot}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="btn btn-outline-dark"
            onClick={() => navigate(`/reports/customer-footprint/new?from=${id}`)}
            disabled={resending}
          >
            Edit &amp; resend
          </button>
          <button
            type="button"
            className={`btn ${report.sent_at ? 'btn-outline-secondary' : 'btn-primary'}`}
            onClick={handleResend}
            disabled={resending}
          >
            {resending ? 'Sending…' : report.sent_at ? 'Resend email' : 'Send email'}
          </button>
        </div>
      </div>

      {!report.sent_at && !resendSuccess && (
        <div className="alert alert-warning" role="alert">
          This report was saved but the email never sent. Click <strong>Send email</strong> to try again.
        </div>
      )}
      {resendError && <div className="alert alert-danger" role="alert">{resendError}</div>}
      {resendSuccess && <div className="alert alert-success" role="alert">{resendSuccess}</div>}

      <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: 12 }}>
        <div className="card-body p-4">
          <div className="row g-3">
            <div className="col-12 col-md-4">
              <div className="text-muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Total carbon
              </div>
              <div className="fw-bold" style={{ fontSize: 28 }}>{formatKg(report.total_emissions_kg)}</div>
            </div>
            <div className="col-6 col-md-4">
              <div className="text-muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Products
              </div>
              <div className="fw-bold" style={{ fontSize: 22 }}>{report.line_items?.length || 0}</div>
            </div>
            <div className="col-6 col-md-4">
              <div className="text-muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Status
              </div>
              <div className="fw-bold" style={{ fontSize: 16, color: report.sent_at ? '#16a34a' : '#a16207' }}>
                {report.sent_at ? 'Delivered' : 'Saved (not sent)'}
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2 className="h6 fw-bold text-muted mt-4 mb-2" style={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 12 }}>
        Email preview
      </h2>
      <div className="card border-0 shadow-sm" style={{ borderRadius: 12, overflow: 'hidden' }}>
        {previewHtml ? (
          <iframe
            title="Report preview"
            srcDoc={previewHtml}
            style={{ width: '100%', height: 800, border: 0, display: 'block' }}
            sandbox=""
          />
        ) : (
          <div className="card-body p-4">
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-end">Qty</th>
                  <th className="text-end">Per unit</th>
                  <th className="text-end">Line total</th>
                </tr>
              </thead>
              <tbody>
                {(report.line_items || []).map((it, i) => (
                  <tr key={i}>
                    <td>
                      <div className="fw-semibold">{it.name}</div>
                      {it.sku && <div className="text-muted" style={{ fontSize: 12 }}>{it.sku}</div>}
                    </td>
                    <td className="text-end">{it.quantity}</td>
                    <td className="text-end">{formatKg(it.emissions_per_unit_kg)}</td>
                    <td className="text-end fw-semibold">{formatKg(it.line_emissions_kg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function CustomerFootprintReportDetail() {
  return (
    <ReportsGate>
      <CustomerFootprintReportDetailInner />
    </ReportsGate>
  );
}
